import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../components/Button'
import { api, type Subscriber, type TestPoolDetail } from '../api/client'
import { fmtRelative } from '../lib/format'

function describeError(err: unknown): string | null {
  if (!err) return null
  if (err instanceof Error) return err.message
  return String(err)
}

export function TestPoolDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const detail = useQuery({
    queryKey: ['test-pool', id],
    queryFn: () => api.get<TestPoolDetail>(`/api/test-pools/${id}`),
    enabled: !!id,
  })

  // Edit pool metadata
  const [editing, setEditing] = useState(false)
  const [editSlug, setEditSlug] = useState('')
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  useEffect(() => {
    if (detail.data) {
      setEditSlug(detail.data.slug)
      setEditName(detail.data.name)
      setEditDescription(detail.data.description || '')
    }
  }, [detail.data])

  const save = useMutation({
    mutationFn: () =>
      api.put<TestPoolDetail>(`/api/test-pools/${id}`, {
        slug: editSlug.trim(),
        name: editName.trim(),
        description: editDescription.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-pool', id] })
      queryClient.invalidateQueries({ queryKey: ['test-pools'] })
      setEditing(false)
    },
  })

  const removePool = useMutation({
    mutationFn: () => api.del<{ ok: boolean }>(`/api/test-pools/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-pools'] })
      navigate('/subscribers/test-pools')
    },
  })

  // Subscriber search → add to pool
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const h = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(h)
  }, [search])

  const subscribers = useQuery({
    queryKey: ['test-pool-subscriber-search', debounced],
    queryFn: () =>
      api.get<{ items: Subscriber[] }>(
        `/api/subscribers?q=${encodeURIComponent(debounced)}&limit=20`,
      ),
    enabled: debounced.length >= 2,
  })

  const addMember = useMutation({
    mutationFn: (subscriber_id: number) =>
      api.post<{ ok: boolean }>(`/api/test-pools/${id}/members`, { subscriber_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-pool', id] })
      queryClient.invalidateQueries({ queryKey: ['test-pools'] })
      setSearch('')
    },
  })

  const removeMember = useMutation({
    mutationFn: (subscriber_id: number) =>
      api.del<{ ok: boolean }>(`/api/test-pools/${id}/members/${subscriber_id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-pool', id] })
      queryClient.invalidateQueries({ queryKey: ['test-pools'] })
    },
  })

  if (detail.isLoading) {
    return <div className="p-8 label text-paper-trace">Loading…</div>
  }
  if (!detail.data) {
    return <div className="p-8 label text-paper-trace">Pool not found.</div>
  }

  const memberIds = new Set(detail.data.members.map((m) => m.subscriber_id))
  const saveError = describeError(save.error)
  const addError = describeError(addMember.error)

  return (
    <div className="p-8 space-y-6 max-w-[1200px]">
      <div className="flex items-center gap-3">
        <Link to="/subscribers/test-pools" className="label-amber hover:underline">← Test Pools</Link>
        <span className="label text-paper-trace">/</span>
        <span className="label">{detail.data.name}</span>
      </div>

      <header className="border-b border-ink-500 pb-6 space-y-3">
        {!editing ? (
          <div className="flex items-end justify-between">
            <div>
              <div className="label mb-1">{detail.data.slug}</div>
              <h1 className="font-serif text-[32px] leading-tight text-paper">{detail.data.name}</h1>
              {detail.data.description && (
                <p className="text-paper-dim mt-2 text-[13px]">{detail.data.description}</p>
              )}
              <div className="text-paper-trace text-[12px] mt-2 font-mono">
                {detail.data.member_count} member{detail.data.member_count === 1 ? '' : 's'}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="md" onClick={() => setEditing(true)}>Edit</Button>
              <Button
                variant="outline"
                size="md"
                onClick={() => {
                  if (window.confirm(`Delete pool "${detail.data!.name}"? Members are not deleted.`)) {
                    removePool.mutate()
                  }
                }}
                className="text-radar-red border-radar-red/60 hover:bg-radar-red/10"
              >
                Delete pool
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="label mb-1">Slug</div>
                <input
                  className="w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[13px]"
                  value={editSlug}
                  onChange={(e) => setEditSlug(e.target.value)}
                />
              </div>
              <div>
                <div className="label mb-1">Name</div>
                <input
                  className="w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper text-[13px]"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </div>
            </div>
            <div>
              <div className="label mb-1">Description</div>
              <textarea
                className="w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper text-[13px] resize-none h-20"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
              />
            </div>
            {saveError && <div className="font-mono text-[12px] text-radar-red">{saveError}</div>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="md" onClick={() => setEditing(false)}>Cancel</Button>
              <Button
                size="md"
                disabled={save.isPending || !editSlug.trim() || !editName.trim()}
                onClick={() => save.mutate()}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </header>

      <section className="space-y-3">
        <h2 className="font-serif text-[20px] text-paper">Add subscriber</h2>
        <input
          type="search"
          className="w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper text-[13px]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email, name, or SimpleCirc account #"
        />
        {addError && <div className="font-mono text-[12px] text-radar-red">{addError}</div>}
        {debounced.length >= 2 && (
          <div className="border border-ink-500 bg-ink-800/40 max-h-[300px] overflow-y-auto">
            {subscribers.isLoading && <div className="p-3 label text-paper-trace">Searching…</div>}
            {subscribers.data && subscribers.data.items.length === 0 && (
              <div className="p-3 label text-paper-trace">No matches.</div>
            )}
            {subscribers.data?.items.map((s) => {
              const inPool = memberIds.has(s.id)
              return (
                <div key={s.id} className="flex items-center gap-3 px-3 py-2 border-b border-ink-500/30 last:border-0 text-[13px] font-mono">
                  <div className="flex-1">
                    <div className="text-paper">
                      {s.email || <span className="text-paper-trace italic">(no email)</span>}
                    </div>
                    <div className="text-[11px] text-paper-trace">
                      {[s.first_name, s.last_name].filter(Boolean).join(' ') || '—'}
                      {s.simplecirc_account_id ? ` · acct ${s.simplecirc_account_id}` : ''}
                    </div>
                  </div>
                  {inPool ? (
                    <span className="label text-paper-trace">already in pool</span>
                  ) : (
                    <button
                      type="button"
                      disabled={addMember.isPending}
                      onClick={() => addMember.mutate(s.id)}
                      className="label-amber hover:underline disabled:opacity-50"
                    >
                      Add →
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-serif text-[20px] text-paper">Members</h2>
        {detail.data.members.length === 0 ? (
          <div className="label text-paper-trace p-6 border border-ink-500 bg-ink-800/40">
            No members yet — search above to add some.
          </div>
        ) : (
          <div className="border border-ink-500 bg-ink-800/40">
            <table className="w-full text-[13px] font-mono">
              <thead>
                <tr className="border-b border-ink-500/60 bg-ink-800/60">
                  <th className="label py-2 px-3 font-normal text-left">Email</th>
                  <th className="label py-2 px-3 font-normal text-left">Name</th>
                  <th className="label py-2 px-3 font-normal text-left">Account #</th>
                  <th className="label py-2 px-3 font-normal text-left">Subscriptions</th>
                  <th className="label py-2 px-3 font-normal text-left">Added</th>
                  <th className="label py-2 px-3 font-normal text-right">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {detail.data.members.map((m) => (
                  <tr key={m.subscriber_id} className="border-b border-ink-500/30 hover:bg-ink-700/30">
                    <td className="px-3 py-2">
                      <Link to={`/subscribers/${m.subscriber_id}`} className="text-paper hover:text-amber-glow">
                        {m.email || <span className="text-paper-trace italic">(no email)</span>}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-paper-dim">
                      {[m.first_name, m.last_name].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="px-3 py-2 text-paper-dim">{m.simplecirc_account_id || '—'}</td>
                    <td className="px-3 py-2 text-paper-dim">
                      {m.publication_slugs.length ? m.publication_slugs.join(', ') : '—'}
                    </td>
                    <td className="px-3 py-2 text-paper-dim">{m.added_at ? fmtRelative(m.added_at) : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        disabled={removeMember.isPending}
                        onClick={() => removeMember.mutate(m.subscriber_id)}
                        className="label text-radar-red hover:underline disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
